import React, { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotificationItem } from "./NotificationItem";
import { useNotifications } from "@/hooks/useNotifications";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

interface NotificationsPopoverProps {
  isMobile?: boolean;
}

export const NotificationsPopover: React.FC<NotificationsPopoverProps> = ({
  isMobile = false,
}) => {
  const { notifications, isLoading, markAsRead, markAllAsRead } = useNotifications();
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();
  const [notifOpen, setNotifOpen] = useState(false);

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <>
      {/* Desktop: Popover, Mobile: Sheet */}
      {!isMobile ? (
        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("notifications")}
              className="relative mr-1"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 flex items-center justify-center h-4 w-4 rounded-full bg-destructive text-[10px] text-white font-bold">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
              <span className="sr-only">{t("notifications")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            align="end" 
            className="w-80 p-0"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm">{t("notifications")}</h3>
                {unreadCount > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {unreadCount} {t("unread")}
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMarkAllAsRead();
                  }}
                  className="h-7 text-xs"
                >
                  {t("markAllAsRead")}
                </Button>
              )}
            </div>
            <ScrollArea className="h-[320px]">
              {isLoading ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {t("loading")}
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">
                    {t("noNotifications")}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      id={notification.id}
                      type={notification.type}
                      message={notification.message}
                      timeAgo={notification.timeAgo}
                      isRead={!!notification.read_at}
                      onMarkAsRead={handleMarkAsRead}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      ) : (
        <>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("notifications")}
            className="relative mr-1"
            onClick={() => setNotifOpen(!notifOpen)}
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 flex items-center justify-center h-4 w-4 rounded-full bg-destructive text-[10px] text-white font-bold">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
            <span className="sr-only">{t("notifications")}</span>
          </Button>
          <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
            <SheetContent
              side="bottom"
              className="p-0"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm">{t("notifications")}</h3>
                  {unreadCount > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {unreadCount} {t("unread")}
                    </span>
                  )}
                </div>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMarkAllAsRead();
                    }}
                    className="h-7 text-xs"
                  >
                    {t("markAllAsRead")}
                  </Button>
                )}
              </div>
              <ScrollArea className="h-[60vh]">
                {isLoading ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    {t("loading")}
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">
                      {t("noNotifications")}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {notifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        id={notification.id}
                        type={notification.type}
                        message={notification.message}
                        timeAgo={notification.timeAgo}
                        isRead={!!notification.read_at}
                        onMarkAsRead={handleMarkAsRead}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </>
      )}
    </>
  );
};
